import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { FormGroupEmail } from '../FormGroupEmail';
import './styles.css';

export const LoginWithPassword = ({ onSwitch, hideSignUp = false, redirectTo = '/setlist' }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setLoading(true);
      
      await signInWithEmailAndPassword(auth, email, password);
      // User will be automatically logged in after successful authentication
      // Redirect to specified page
      if (redirectTo) {
        navigate(redirectTo);
      }
      // If redirectTo is null, stay on current page (component will re-render with user logged in)
      
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email. Please sign up.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError('Failed to log in: ' + error.message);
      }
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address to reset your password.');
      return;
    }
    
    try {
      setError('');
      setResetLoading(true);
      
      await sendPasswordResetEmail(auth, email, {
        url: window.location.origin,
        handleCodeInApp: false,
      });
      
      setResetEmailSent(true);
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else {
        setError('Failed to send reset email: ' + error.message);
      }
    }
    setResetLoading(false);
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setResetEmailSent(false);
    setError('');
  };

  return (
    <div className="login-container">
      <form onSubmit={showForgotPassword ? handleForgotPassword : handleSubmit} className="login-form">
        <h2>{showForgotPassword ? 'Reset Password' : 'Welcome Back'}</h2>
        
        {error && <div className="error-message">{error}</div>}
        
        {resetEmailSent ? (
          <div className="success-message">
            <p>Password reset email sent to {email}!</p>
            <p>Check your inbox and follow the link to reset your password.</p>
            <button 
              type="button" 
              className="back-button" 
              onClick={handleBackToLogin}
            >
              Back to Login
            </button>
          </div>
        ) : (
          <>
            <FormGroupEmail 
              email={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            {!showForgotPassword && (
              <div className="form-group">
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder=" "
                />
                <label htmlFor="password">Password</label>
              </div>
            )}

            <button 
              type="submit" 
              className="submit-button" 
              disabled={showForgotPassword ? resetLoading : loading}
            >
              {showForgotPassword 
                ? (resetLoading ? 'Sending...' : 'Send Reset Email')
                : (loading ? 'Logging in...' : 'Log In')
              }
            </button>

            {showForgotPassword ? (
              <button 
                type="button" 
                className="forgot-password-link" 
                onClick={handleBackToLogin}
              >
                ← Back to Login
              </button>
            ) : (
              <button 
                type="button" 
                className="forgot-password-link" 
                onClick={() => setShowForgotPassword(true)}
              >
                Forgot Password?
              </button>
            )}

            {!showForgotPassword && !hideSignUp && (
              <p className="switch-auth">
                Don't have an account?{' '}
                <button type="button" onClick={onSwitch} className="switch-button">
                  Sign Up
                </button>
              </p>
            )}
          </>
        )}
      </form>
    </div>
  );
};

LoginWithPassword.propTypes = {
  onSwitch: PropTypes.func.isRequired,
  hideSignUp: PropTypes.bool,
  redirectTo: PropTypes.string,
};
